/**
 * ScopeChipPicker (Phase 24.7 / Track 1)
 *
 * Reusable multi-select chip picker for KPI scope (zones + positions).
 * Selected items render as removable chips. Unselected options live in a
 * dropdown that filters out items already picked.
 *
 * Inactive options (e.g., a zone deactivated after the KPI was scoped to it)
 * may appear in `selectedIds` but are excluded from the "add" dropdown — they
 * can be removed but never re-added. A "(inactive)" suffix flags them in the
 * chip label so trainers can see what's stale.
 *
 * When `required && selectedIds.length === 0`, an inline blocking error
 * surfaces below the picker, mirroring the Zod error message from
 * `kpiBuilderResultSchema.stationIds.min(1, ...)`.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, X, AlertCircle, Search } from 'lucide-react';

interface ScopeOption {
  id: string;
  name: string;
  /** Optional — defaults to true. False indicates a stale/deactivated option. */
  isActive?: boolean;
}

interface ScopeChipPickerProps {
  /** Field label rendered above the picker. */
  label: string;
  /** All choices the trainer can pick from (typically a lookup endpoint result). */
  options: ScopeOption[];
  /** Currently selected ids — drives chip rendering. */
  selectedIds: string[];
  /** Called whenever the user adds or removes a chip. */
  onChange: (ids: string[]) => void;
  /** When true, an inline error appears if `selectedIds` is empty. */
  required?: boolean;
  /** Placeholder for the dropdown. Defaults to 'Select…'. */
  placeholder?: string;
  /** Additional copy under the picker (e.g. "Optional. Empty = all positions."). */
  helpText?: string;
  /** Custom error message override (defaults to "Pick at least one zone"). */
  errorMessage?: string;
  /** Disable interaction (e.g. while a parent mutation is pending). */
  disabled?: boolean;
}

export function ScopeChipPicker({
  label,
  options,
  selectedIds,
  onChange,
  required = false,
  placeholder = 'Select…',
  helpText,
  errorMessage = 'Pick at least one zone',
  disabled = false,
}: ScopeChipPickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click (mousedown capture-phase, so the dropdown
  // unmounts before the click event reaches its target — otherwise the still-
  // rendered dropdown overlay can intercept clicks on buttons positioned below
  // it, e.g. the modal's "Save KPI" button — Apr 28, 2026 KPI v2 QA finding)
  // or on Escape.
  useEffect(() => {
    if (!isOpen) return;
    const onMouseDown = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setSearch('');
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', onMouseDown, true);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onMouseDown, true);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen]);

  // Map id → option for chip rendering. Selected ids that are NOT in `options`
  // (e.g. a zone hidden from lookup because isActive=false) still render as
  // chips with a fallback label so the trainer can remove them.
  const optionsById = useMemo(() => {
    const map = new Map<string, ScopeOption>();
    for (const o of options) map.set(o.id, o);
    return map;
  }, [options]);

  // Dropdown candidates: not-already-selected AND active. Stale entries can be
  // removed but cannot be re-added.
  const dropdownOptions = useMemo(() => {
    const selectedSet = new Set(selectedIds);
    const q = search.trim().toLowerCase();
    return options.filter((o) => {
      if (selectedSet.has(o.id)) return false;
      if (o.isActive === false) return false;
      if (q && !o.name.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [options, selectedIds, search]);

  const handleAdd = (id: string) => {
    if (selectedIds.includes(id)) return;
    onChange([...selectedIds, id]);
    setSearch('');
    // Keep dropdown open for multi-pick — closing is one click on the chevron.
  };

  const handleRemove = (id: string) => {
    onChange(selectedIds.filter((sid) => sid !== id));
  };

  const showError = required && selectedIds.length === 0;

  return (
    <div className="space-y-2">
      <div className="flex items-baseline justify-between gap-3">
        <label className="text-sm font-medium text-gray-700">
          {label}
          {required && <span className="ml-1 text-red-500">*</span>}
        </label>
        {helpText && (
          <span className="text-xs text-gray-500">{helpText}</span>
        )}
      </div>

      <div ref={wrapperRef} className="relative">
        {/* Selected chips + add-trigger (input-styled wrapper) */}
        <div
          className={`
            flex flex-wrap items-center gap-1.5 rounded-xl border bg-white px-3 py-2
            transition-colors
            ${showError ? 'border-red-300 focus-within:border-red-400' : 'border-gray-200 focus-within:border-guild-green'}
            ${disabled ? 'opacity-60 cursor-not-allowed' : ''}
          `}
        >
          {selectedIds.length === 0 && (
            <span className="text-sm text-gray-400">No selection</span>
          )}

          {selectedIds.map((id) => {
            const opt = optionsById.get(id);
            const name = opt?.name ?? id.slice(0, 8);
            const inactive = opt?.isActive === false;
            return (
              <span
                key={id}
                className={`
                  inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium
                  ${inactive
                    ? 'bg-amber-50 text-amber-700 ring-1 ring-amber-200'
                    : 'bg-guild-mint/40 text-guild-green ring-1 ring-guild-green/15'
                  }
                `}
              >
                {name}
                {inactive && <span className="text-amber-600">(inactive)</span>}
                {!disabled && (
                  <button
                    type="button"
                    onClick={() => handleRemove(id)}
                    className="ml-0.5 -mr-0.5 rounded-full p-0.5 hover:bg-black/5 transition-colors"
                    aria-label={`Remove ${name}`}
                  >
                    <X size={11} />
                  </button>
                )}
              </span>
            );
          })}

          {!disabled && (
            <button
              type="button"
              onClick={() => setIsOpen((v) => !v)}
              className="ml-auto inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs text-gray-500 hover:bg-gray-100 transition-colors"
              aria-expanded={isOpen}
            >
              {selectedIds.length > 0 ? 'Add' : placeholder}
              <ChevronDown size={12} className={isOpen ? 'rotate-180 transition-transform' : 'transition-transform'} />
            </button>
          )}
        </div>

        {/* Dropdown */}
        {isOpen && !disabled && (
          <div className="absolute left-0 right-0 top-full z-30 mt-1 max-h-60 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-lg">
            <div className="relative border-b border-gray-100 p-2">
              <Search size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search…"
                autoFocus
                className="w-full rounded-lg border border-gray-200 bg-white pl-7 pr-2 py-1.5 text-xs focus:border-guild-green focus:ring-1 focus:ring-guild-green/30 outline-none"
              />
            </div>
            <div className="max-h-48 overflow-y-auto py-1">
              {dropdownOptions.length === 0 ? (
                <div className="px-3 py-2 text-xs text-gray-400">
                  {options.length === 0
                    ? 'Nothing to pick yet.'
                    : search
                      ? 'No matches.'
                      : 'All options selected.'}
                </div>
              ) : (
                dropdownOptions.map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => handleAdd(opt.id)}
                    className="flex w-full items-center justify-between px-3 py-1.5 text-left text-xs text-gray-700 hover:bg-guild-mint/30 transition-colors"
                  >
                    <span>{opt.name}</span>
                  </button>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      {showError && (
        <div className="flex items-center gap-1.5 text-xs text-red-600">
          <AlertCircle size={12} />
          {errorMessage}
        </div>
      )}
    </div>
  );
}
